/**
 * Locales index
 * Экспорт всех локалей
 */
import ru from './ru'
import en from './en'
import vn from './vn'
import { routingOnboardingMessages } from './routingOnboarding'
import { agentOnboardingMessages } from './agentOnboarding'
import { agentTicketsMessages } from './agentTickets'
import { agentsAdminMessages } from './agentsAdmin'
import { agentInquiriesMessages } from './agentInquiries'
import { agentTasksMessages } from './agentTasks'
import { agentCabinetMessages } from './agentCabinet'
import { companyVerificationsMessages } from './companyVerifications'
import { impersonationMessages } from './impersonation'

type AnyRecord = Record<string, unknown>

/**
 * Рекурсивное слияние словарей переводов. Объекты сливаются вглубь, прочие
 * значения (строки и т.п.) перекрываются значением из override. Используется,
 * чтобы подмешать feature-строки (routing- и agent-онбординг) поверх базовых
 * локалей, включая переопределение примитива объектом (fields.primaryValueHint).
 */
function deepMerge<T extends AnyRecord>(base: T, override: AnyRecord): T {
  const out: AnyRecord = { ...base }

  for (const [key, value] of Object.entries(override)) {
    const prev = out[key]
    const bothObjects =
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      prev !== null &&
      typeof prev === 'object' &&
      !Array.isArray(prev)

    out[key] = bothObjects ? deepMerge(prev as AnyRecord, value as AnyRecord) : value
  }

  return out as T
}

const baseMessages = {
  ru: deepMerge(
    deepMerge(
      deepMerge(
        deepMerge(
          deepMerge(deepMerge(ru, routingOnboardingMessages.ru), agentOnboardingMessages.ru),
          agentTicketsMessages.ru,
        ),
        agentsAdminMessages.ru,
      ),
      agentInquiriesMessages.ru,
    ),
    deepMerge(
      deepMerge(agentTasksMessages.ru, companyVerificationsMessages.ru),
      agentCabinetMessages.ru,
    ),
  ),
  en: deepMerge(
    deepMerge(
      deepMerge(
        deepMerge(
          deepMerge(deepMerge(en, routingOnboardingMessages.en), agentOnboardingMessages.en),
          agentTicketsMessages.en,
        ),
        agentsAdminMessages.en,
      ),
      agentInquiriesMessages.en,
    ),
    deepMerge(
      deepMerge(agentTasksMessages.en, companyVerificationsMessages.en),
      agentCabinetMessages.en,
    ),
  ),
  vn: deepMerge(
    deepMerge(
      deepMerge(
        deepMerge(
          deepMerge(deepMerge(vn, routingOnboardingMessages.vn), agentOnboardingMessages.vn),
          agentTicketsMessages.vn,
        ),
        agentsAdminMessages.vn,
      ),
      agentInquiriesMessages.vn,
    ),
    deepMerge(
      deepMerge(agentTasksMessages.vn, companyVerificationsMessages.vn),
      agentCabinetMessages.vn,
    ),
  ),
}

// DEV-2400: строки вкладки просмотра поверх остальных словарей. Отдельным шагом,
// а не ещё одним уровнем скобок в дереве выше.
export const messages = {
  ru: deepMerge(baseMessages.ru, impersonationMessages.ru),
  en: deepMerge(baseMessages.en, impersonationMessages.en),
  vn: deepMerge(baseMessages.vn, impersonationMessages.vn),
}

export type Locale = keyof typeof messages
export type MessageSchema = typeof ru

export const availableLocales: Array<{ code: Locale; name: string; flag: string }> = [
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'vn', name: 'Tiếng Việt', flag: '🇻🇳' },
]
